import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

/** Seconds before expiry at which the warning first appears */
const WARN_BEFORE_SECONDS = 5 * 60; // 5 minutes

/** How often (ms) we re-check the token expiry */
const CHECK_INTERVAL_MS = 15_000; // every 15s

function getTokenExpiry(token: string | null): number | null {
  if (!token) return null;
  try {
    const parts = token.split('.');
    const payload = parts[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof decoded.exp === 'number' ? decoded.exp : null;
  } catch {
    return null;
  }
}

function formatSecondsLeft(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m > 0) return `${m}:${s.toString().padStart(2, '0')}`;
  return `${s}s`;
}

const SessionExpiryWarning: React.FC = () => {
  const { user, logout } = useAuth();
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const checkExpiry = useCallback(() => {
    const token = localStorage.getItem('token');
    const exp = getTokenExpiry(token);
    if (!exp) { setSecondsLeft(null); return; }

    const remaining = exp - Math.floor(Date.now() / 1000);

    if (remaining <= 0) {
      // Token already expired — let the API interceptor handle it
      setSecondsLeft(0);
      return;
    }

    if (remaining <= WARN_BEFORE_SECONDS) {
      setSecondsLeft(remaining);
      setDismissed(false); // Re-show if it was previously dismissed and now near expiry again
    } else {
      setSecondsLeft(null);
    }
  }, []);

  // Run check on mount and on interval
  useEffect(() => {
    if (!user) { setSecondsLeft(null); return; }
    checkExpiry();
    const timer = setInterval(checkExpiry, CHECK_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [user, checkExpiry]);

  // Also recheck when token is refreshed via other means
  useEffect(() => {
    const handler = () => { setDismissed(false); checkExpiry(); };
    window.addEventListener('tokenRefreshed', handler);
    return () => window.removeEventListener('tokenRefreshed', handler);
  }, [checkExpiry]);

  const handleStayLoggedIn = async () => {
    setRefreshing(true);
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) { await logout(); return; }

      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });

      if (res.ok) {
        const data = await res.json();
        if (data.token) {
          localStorage.setItem('token', data.token);
          if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
          window.dispatchEvent(new CustomEvent('tokenRefreshed'));
          setSecondsLeft(null);
          setDismissed(true);
        } else {
          await logout();
        }
      } else {
        await logout();
      }
    } catch {
      // Fail gracefully — user will get a 401 on the next request
    } finally {
      setRefreshing(false);
    }
  };

  if (!user || secondsLeft === null || dismissed) return null;

  const isUrgent = secondsLeft <= 60;

  return (
    <div className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-[10000] flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl border max-w-sm w-full mx-4 transition-all ${
      isUrgent
        ? 'bg-red-50 border-red-300 text-red-800'
        : 'bg-amber-50 border-amber-300 text-amber-800'
    }`}>
      <div className={`text-lg font-black tabular-nums tracking-tight shrink-0 ${isUrgent ? 'text-red-600' : 'text-amber-600'}`}>
        {formatSecondsLeft(secondsLeft)}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold">Session expiring soon</p>
        <p className="text-[11px] opacity-75 leading-tight">Stay logged in or your work may be lost.</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={handleStayLoggedIn}
          disabled={refreshing}
          className={`text-[11px] font-bold px-3 py-1.5 rounded-lg transition-colors ${
            isUrgent
              ? 'bg-red-600 text-white hover:bg-red-700 disabled:opacity-60'
              : 'bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60'
          }`}
        >
          {refreshing ? '...' : 'Stay'}
        </button>
        <button
          onClick={() => logout()}
          className="text-[11px] font-semibold opacity-60 hover:opacity-100 transition-opacity"
        >
          Logout
        </button>
      </div>
    </div>
  );
};

export default SessionExpiryWarning;
